import { createContext, useCallback, useContext, useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { disablePush } from "@/lib/push";

const useBrandHead = (brand) => {
  useEffect(() => {
    if (!brand) return;
    const v = brand.has_logo ? brand.logo_version : "default";
    document.title = `${brand.company_name} · WhatsApp CRM`;
    document.querySelector('link[rel="icon"]')?.setAttribute("href", `/api/branding/icon/64.png?v=${v}`);
    document.querySelector('link[rel="apple-touch-icon"]')?.setAttribute("href", `/api/branding/icon/180.png?v=${v}`);
    document.querySelector('meta[name="apple-mobile-web-app-title"]')?.setAttribute("content", brand.company_name.slice(0, 14));
  }, [brand]);
};

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);

  useEffect(() => {
    api.get("/auth/me").then((r) => setUser(r.data)).catch(() => setUser(false));
    const onLogout = () => setUser(false);
    window.addEventListener("auth:logout", onLogout);
    return () => window.removeEventListener("auth:logout", onLogout);
  }, []);

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    localStorage.setItem("access_token", data.access_token);
    setUser(data);
  };

  const logout = useCallback(async () => {
    await disablePush().catch(() => {});
    await api.post("/auth/logout").catch(() => {});
    localStorage.removeItem("access_token");
    setUser(false);
  }, []);

  return <AuthContext.Provider value={{ user, login, logout }}>{children}</AuthContext.Provider>;
};

export const useAuth = () => useContext(AuthContext);

export const useBranding = () => {
  const q = useQuery({ queryKey: ["branding"], queryFn: () => api.get("/branding").then((r) => r.data) });
  useBrandHead(q.data);
  return q;
};

export const useUsers = () =>
  useQuery({ queryKey: ["users"], queryFn: () => api.get("/users").then((r) => r.data) });

export const useTags = () =>
  useQuery({ queryKey: ["tags"], queryFn: () => api.get("/tags").then((r) => r.data) });
